import {ErrorAction} from '@/services/ErrorService/ErrorService.constants';

import {ErrorCode, HttpStatus} from '@specials/project-user';

import {BonusRedirectType, NamedItem, StatisticItemValidationType} from '@/types';

import config from './config';
import content from './content';
import {errors} from './errors';
import {mockStatisticContent, mockStatisticItems, mockStatisticValidationConfig} from './mockStatistic';
import {statisticContent} from './statisticContent';
import {statisticDefault} from './statisticDefault';
import {statisticItems} from './statisticItems';
import {statisticValidationConfig} from './statisticValidationConfig';

const isProjectApiMockEnabled = process.env.APP_PROJECT_API_MOCK_ENABLED === 'true';

export const resources = {
  config,
  content,
  errors,
  statisticItems: isProjectApiMockEnabled ? mockStatisticItems : statisticItems,
  statisticContent: isProjectApiMockEnabled ? mockStatisticContent : statisticContent,
  statisticValidationConfig: isProjectApiMockEnabled ? mockStatisticValidationConfig : statisticValidationConfig,
  statisticDefault,
};

export type ResourcesAll = typeof resources;

export type IStatisticConfig = {
  parameterName: string;
  content: IStatisticContent;
};

type IStatisticContent = {
  main: IStatisticMainContent;
  description: IStatisticDescriptionContent;
};

type IStatisticMainContent = {
  title: string;
  text: string;
  plurals: string[];
};

type IStatisticDescriptionContent = {
  text: string;
  plurals: string[];
};

export type IResourcesStatisticItem = {
  title: string;
  imageLarge: {
    alt: string;
    src: {
      png: string;
      avif: string;
      webp: string;
    };
  };
  imageSmall: {
    alt: string;
    src: {
      png: string;
      avif: string;
      webp: string;
    };
  };
  imageSmallOpened: {
    alt: string;
    src: {
      png: string;
      avif: string;
      webp: string;
    };
  };
  defaultStatistic: {
    title: string;
    description: string;
  } | null;
  extraDescription: string;
  bonusRedirectType: BonusRedirectType;
  wishes: {
    title: string;
    text: string;
    avatar: {
      src: {
        png: string;
        avif: string;
        webp: string;
      };
      alt: string;
    };
    buttonLink: {
      text: string;
      href: string;
    } | null;
  };
};

export type NamedStatisticContent = NamedItem<IStatisticConfig>;

export type NamedResourcesStatisticItem = NamedItem<IResourcesStatisticItem>;

export type NamedValidationConfig = NamedItem<StatisticValidationConfig>;

export type NamedStatisticDefault = Pick<
  NamedResourcesStatisticItem,
  'name' | 'title' | 'imageLarge' | 'defaultStatistic' | 'extraDescription'
>;

export type StatisticValidationConfig = {
  rules: StatisticValidationRule[];
};

export type StatisticValidationRule =
  | MinStringLengthValidationRule
  | MaxStringLengthValidationRule
  | NonNegativeValidationRule
  | SignsAfterCommaValidationRule
  | IntegerValidationRule
  | UpperCaseStringValidationRule
  | MoreThanValueValidationRule
  | LessThanValueValidationRule
  | BlackListStringValidationRule
  | DayMonthDateValidationRule
  | MonthDateValidationRule
  | SecondsValidationRule
  | WhiteListNumberValidationRule
  | RoundNumberValidationRule;

export type MinStringLengthValidationRule = {
  name: StatisticItemValidationType.MinStringLength;
  value: number;
};

export type MaxStringLengthValidationRule = {
  name: StatisticItemValidationType.MaxStringLength;
  value: number;
};

export type NonNegativeValidationRule = {
  name: StatisticItemValidationType.NonNegative;
  value: boolean;
};

export type SignsAfterCommaValidationRule = {
  name: StatisticItemValidationType.SignsAfterComma;
  value: number;
};

export type IntegerValidationRule = {
  name: StatisticItemValidationType.Integer;
  value: boolean;
};

export type UpperCaseStringValidationRule = {
  name: StatisticItemValidationType.UpperCaseString;
  value: boolean;
};

export type MoreThanValueValidationRule = {
  name: StatisticItemValidationType.MoreThanValue;
  value: number;
};

export type LessThanValueValidationRule = {
  name: StatisticItemValidationType.LessThanValue;
  value: number;
};

export type BlackListStringValidationRule = {
  name: StatisticItemValidationType.BlackListString;
  value: Set<string>;
};

export type DayMonthDateValidationRule = {
  name: StatisticItemValidationType.DayMonthDate;
  value: boolean;
};

export type MonthDateValidationRule = {
  name: StatisticItemValidationType.MonthDate;
  value: boolean;
};

export type SecondsValidationRule = {
  name: StatisticItemValidationType.Seconds;
  value: number;
};

export type WhiteListNumberValidationRule = {
  name: StatisticItemValidationType.WhiteListNumber;
  value: Set<number>;
};

export type RoundNumberValidationRule = {
  name: StatisticItemValidationType.RoundNumber;
  value: boolean;
};

export interface ErrorResourcesContent {
  title: string;
  buttonText?: string;
  action?: ErrorAction;
  isErrorCodeVisible?: boolean;
  text?: string;
}

export interface ErrorResourcesBase {
  mb: ErrorResourcesContent;
  web: ErrorResourcesContent;
}

interface ErrorResourcesErrorCodes extends ErrorResourcesBase {
  errorCodes: ErrorCode[];
}

interface ErrorResourcesHttpCodes extends ErrorResourcesBase {
  httpCodes: HttpStatus[];
}

export interface ResourcesErrors {
  errorCodes: ErrorResourcesErrorCodes[];
  httpCodes: ErrorResourcesHttpCodes[];
  fallback: ErrorResourcesBase;
}
